import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

/**
 * Disabled button directive
 * @class
 * @implements OnInit
 * @implements OnDestroy
 * @desccription it permits to disable a button according a BehaviorSubject<boolean> {@link BehaviorSubject}, if user click on the button, it turns disabled. And then if Subject changes on 'false', it returns at initial state.
 * @example
 * In template component:
 * ```angular2html
 *  * <button disableButton [reEnableButton]="IsLoadingTypedByABoolean"></button>
 * ```
 * In component:
 * ```typescript
 * @Component({
 *   selector: 'what-ever-component'
 *   [...]
 * })
 * public class WhateverComponent {
 *   protected isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>;
 *   [...]
 *   public getDataFromEndpoint(): void {
 *     this.isLoading.next(true):
 *     this.service.getData().subscribe({
 *       next: () => {
 *          this.isLoading.next(false);
 *          // Here's your happiness code...
 *       },
 *       error: () => {
 *          this.isLoading.next(false);
 *          // Here's your sadness code...
 *       },
 *     })
 *   }
 * }
 * ```
 */
@Directive({
  selector: '[disableOnClick]',
  standalone: true,
})
export class DisableOnClickDirective implements OnInit, OnDestroy {
  @Input({ alias: 'reEnableButton', required: true }) reEnableButton: BehaviorSubject<boolean>;
  public subscription = new Subscription();
  private readonly disallowedClass: string = 'disallowed';

  constructor(
    private renderer: Renderer2,
    private element: ElementRef,
  ) {}

  isButton = (): boolean => this.element.nativeElement instanceof HTMLButtonElement;

  @HostListener('click') onClick() {
    this.disableButton();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.reEnableButton.subscribe((value) => {
        if (!value) this.enableButton();
      }),
    );
  }

  private disableButton() {
    if (this.isButton()) {
      this.renderer.setAttribute(this.element.nativeElement, 'disabled', 'true');
      this.renderer.addClass(this.element.nativeElement, this.disallowedClass);
    }
  }

  private enableButton() {
    if (this.isButton()) {
      this.renderer.removeAttribute(this.element.nativeElement, 'disabled');
      this.renderer.removeClass(this.element.nativeElement, this.disallowedClass);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
